import { Component } from '@theme/component';
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
  calculateHeaderGroupHeight,
} from '@theme/utilities';
// `getIntersectionRoot` no se importa: Esprit reemplazo la deteccion de sticky por
// IntersectionObserver con un umbral sobre el scroll del documento (#scrollThreshold).
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop', 'mobileMenuToggle', 'mobileMenuDrawer', 'mobileMenuClose', 'mobileMenuOverlay', 'mobileMenuBody'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * Ultima altura observada del header. Permite restaurar `--header-height` al reconectar
   * sin volver a medir el layout (el editor de temas reconecta el componente en cada edicion).
   * @type {number}
   */
  #height = 0;

  /**
   * Difiere la primera lectura de scroll y de meta theme-color hasta que el ResizeObserver
   * entrega geometria real. Hacerlas en connectedCallback forzaba un reflow sincronico en la
   * ruta critica de render.
   * @type {boolean}
   */
  #metaThemeColorInitialized = false;

  /**
   * Target al que se ata el listener de scroll. En 4.0.0 puede ser `.page-wrapper` en vez de
   * `document`, segun lo que resuelva getScrollEventTarget().
   *
   * No se traen `#intersectionObserver` ni `#offscreen` del vendor: Esprit reemplazo esa
   * deteccion por un umbral sobre el scroll (#scrollThreshold), y ninguno tendria consumidor.
   * @type {EventTarget | null}
   */
  #scrollContainer = null;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Minimum document scroll needed before the home header switches state.
   * This avoids micro-scroll jitter while still making the transition depend
   * on real document scroll instead of the header's viewport position.
   * @constant {number}
   */
  #scrollThreshold = 8;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    // Se usa solo la geometria que ya trae el entry. El fallback que medía el layout de forma
    // sincronica costaba un reflow por cada notificacion del observer.
    const borderBoxSize = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;
    const height = borderBoxSize?.blockSize ?? entry.contentRect.height;
    if (!height) return;

    // Primera pasada: el layout ya existe, asi que aca se hace la lectura inicial de scroll y
    // se publica el meta theme-color, antes de invalidar la altura mas abajo.
    if (!this.#metaThemeColorInitialized) {
      const scrollTop = this.#getScrollTop();
      if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      this.#metaThemeColorInitialized = true;
      this.#updateScrollState(scrollTop);
    }

    this.#height = height;
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Returns the current document scroll position.
   * @returns {number}
   */
  // Delega en scroll-container: en desktop (>=990px) 4.0.0 scrollea dentro de `.page-wrapper`,
  // donde `document.scrollingElement.scrollTop` queda clavado en 0 y el sticky nunca se activaria.
  // Se conserva el clamp de Esprit, que protege del overscroll negativo de iOS.
  #getScrollTop = () => Math.max(0, getScrollTop());

  /**
   * Clears the pending hide timeout for scroll-up sticky mode.
   */
  #clearHideTimeout = () => {
    if (!this.#timeout) return;

    clearTimeout(this.#timeout);
    this.#timeout = null;
  };

  /**
   * Updates the header sticky datasets with a single source of truth.
   * @param {'inactive' | 'active' | 'idle'} stickyState
   * @param {'none' | 'up' | 'down'} scrollDirection
   */
  #setStickyPresentation = (stickyState, scrollDirection) => {
    const stateChanged = this.dataset.stickyState !== stickyState;
    const directionChanged = this.dataset.scrollDirection !== scrollDirection;

    if (stateChanged) {
      this.dataset.stickyState = stickyState;
      // Se publica el meta theme-color recien despues de la pasada inicial del ResizeObserver,
      // para no emitirlo antes de que el componente tenga geometria.
      if (this.#metaThemeColorInitialized && this.dataset.themeColor) {
        changeMetaThemeColor(this.dataset.themeColor);
      }
    }

    if (directionChanged) {
      this.dataset.scrollDirection = scrollDirection;
    }
  };

  /**
   * Updates sticky state for `sticky="always"` based on real document scroll.
   * @param {number} scrollTop
   */
  #updateAlwaysStickyState = (scrollTop) => {
    const hasScrolled = scrollTop > this.#scrollThreshold;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    this.removeAttribute('data-animating');
    this.#clearHideTimeout();

    this.#setStickyPresentation(
      hasScrolled ? 'active' : 'inactive',
      hasScrolled ? (isScrollingUp ? 'up' : 'down') : 'none'
    );

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Updates sticky state for `sticky="scroll-up"` based on real document scroll.
   * @param {number} scrollTop
   */
  #updateScrollUpStickyState = (scrollTop) => {
    const headerHeight = this.getBoundingClientRect().height;
    const hasScrolled = scrollTop > this.#scrollThreshold;
    const hasPassedHeader = scrollTop > headerHeight;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (!hasScrolled || !hasPassedHeader) {
      this.removeAttribute('data-animating');
      this.#clearHideTimeout();
      this.#setStickyPresentation('inactive', 'none');
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');
      this.#clearHideTimeout();
      this.#setStickyPresentation('active', 'up');
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (this.dataset.stickyState === 'active') {
      this.setAttribute('data-animating', '');
      this.#setStickyPresentation('active', 'none');
      this.#clearHideTimeout();

      this.#timeout = setTimeout(() => {
        this.#setStickyPresentation('idle', 'none');
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.#setStickyPresentation('idle', 'none');
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Al cruzar los 990px cambia quien scrollea (`.page-wrapper` <-> `document`), asi que hay
    // que reatar el listener al nuevo target. Se mantiene `{ passive: true }` de Esprit.
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }

    // El vendor recreaba aca su IntersectionObserver. Esprit no lo usa: detecta el sticky con
    // #scrollThreshold sobre la posicion de scroll, que el listener rebindeado ya provee.
    // Se resincroniza el estado para no quedar con el del breakpoint anterior.
    this.#lastScrollTop = this.#getScrollTop();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  /**
   * @param {number} [scrollTop] - Posicion de scroll ya leida. Se pasa desde la pasada inicial
   * del ResizeObserver para no repetir la lectura; el resto de los llamadores la omite.
   */
  #updateScrollState = (scrollTop = this.#getScrollTop()) => {
    const stickyMode = this.getAttribute('sticky');

    if (stickyMode === 'always') {
      this.#updateAlwaysStickyState(scrollTop);
      return;
    }

    if (stickyMode === 'scroll-up') {
      this.#updateScrollUpStickyState(scrollTop);
    }
  };

  /**
   * Opens the mobile menu drawer and moves dynamic content if needed
   */
  #openMobileMenu = () => {
    const drawer = Array.isArray(this.refs.mobileMenuDrawer) ? this.refs.mobileMenuDrawer[0] : this.refs.mobileMenuDrawer;
    if (drawer) drawer.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');

    // Move dynamic menu content if it exists and hasn't been moved yet
    const dynamicContent = document.querySelector('[data-mobile-menu-content]');
    const body = Array.isArray(this.refs.mobileMenuBody) ? this.refs.mobileMenuBody[0] : this.refs.mobileMenuBody;
    if (dynamicContent && body && !body.contains(dynamicContent)) {
      body.appendChild(dynamicContent);
    }
  };

  /**
   * Closes the mobile menu drawer
   */
  #closeMobileMenu = () => {
    const drawer = Array.isArray(this.refs.mobileMenuDrawer) ? this.refs.mobileMenuDrawer[0] : this.refs.mobileMenuDrawer;
    if (drawer) drawer.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
  };

  connectedCallback() {
    super.connectedCallback();

    // Al reconectar (el editor de temas lo hace en cada edicion) se restaura la ultima altura
    // conocida desde la custom property, en vez de volver a medir el layout.
    const initialHeight = parseFloat(document.body.style.getPropertyValue('--header-height'));
    if (!this.#height && initialHeight) this.#height = initialHeight;

    // Se observa la fila superior y no el componente entero: el drawer movil es position:fixed
    // y no aporta altura, asi que el valor medido es el mismo y se evitan notificaciones por
    // cambios de caja del propio componente.
    this.#resizeObserver.observe(this.refs.headerRowTop);
    if (this.#height) document.body.style.setProperty('--header-height', `${this.#height}px`);

    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      // El estado sticky inicial NO se calcula aca: leer el scroll en connectedCallback forzaba
      // un reflow sincronico en la ruta critica. La primera pasada del ResizeObserver lo hace,
      // ya con layout disponible.
      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        // 4.0.0 introduce `.page-wrapper`: en desktop (>=990px) el scroll ocurre ahi y no en
        // `document`, asi que hay que escuchar el target que devuelve getScrollEventTarget().
        // Fuera de ese caso devuelve `document` y el comportamiento es el de siempre.
        // `{ passive: true }` es de Esprit y se conserva: el handler no llama preventDefault.
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }

    // Mobile menu events
    const toggle = Array.isArray(this.refs.mobileMenuToggle) ? this.refs.mobileMenuToggle[0] : this.refs.mobileMenuToggle;
    const close = Array.isArray(this.refs.mobileMenuClose) ? this.refs.mobileMenuClose[0] : this.refs.mobileMenuClose;
    const overlay = Array.isArray(this.refs.mobileMenuOverlay) ? this.refs.mobileMenuOverlay[0] : this.refs.mobileMenuOverlay;

    toggle?.addEventListener('click', this.#openMobileMenu);
    close?.addEventListener('click', this.#closeMobileMenu);
    overlay?.addEventListener('click', this.#closeMobileMenu);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#clearHideTimeout();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;

    const toggle = Array.isArray(this.refs.mobileMenuToggle) ? this.refs.mobileMenuToggle[0] : this.refs.mobileMenuToggle;
    const close = Array.isArray(this.refs.mobileMenuClose) ? this.refs.mobileMenuClose[0] : this.refs.mobileMenuClose;
    const overlay = Array.isArray(this.refs.mobileMenuOverlay) ? this.refs.mobileMenuOverlay[0] : this.refs.mobileMenuOverlay;

    toggle?.removeEventListener('click', this.#openMobileMenu);
    close?.removeEventListener('click', this.#closeMobileMenu);
    overlay?.removeEventListener('click', this.#closeMobileMenu);
    document.body.classList.remove('overflow-hidden');

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    // El editor de temas desconecta y reconecta el componente en la misma tarea. Si se pone la
    // altura en 0 de inmediato, el resto del layout colapsa y rebota. Se difiere un microtask y
    // solo se aplica si el componente sigue desconectado de verdad.
    queueMicrotask(() => {
      if (!this.isConnected) document.body.style.setProperty('--header-height', '0px');
    });
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      const headerGroupHeight = calculateHeaderGroupHeight(header, headerGroup);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
